import { getAnalytics } from "@tm/insurance/analytics";
import { convertUnixToDate } from "@tm/insurance/utils";
import { MONTHS } from "@tm/isomorphic-utils";
import { buildRecentSearchNextPageRequest } from "../../utils/recentSearchNavigation";
import { theme } from "@tm/utility/global";
import { useEffect } from "react";
import { IRecentSearches } from "../../pages/InsuranceCommonLanding/types";
import FlexComp from "../FlexComp/FlexComp";
import FormattedText from "../FormattedText/FormattedText";
import { CountryNameText, RecentSearchCard } from "./RecentSearches.style";

const RecentSearches = ({
  recentSearchInfo,
  nextPageDataCallTrigger,
}: {
  recentSearchInfo: IRecentSearches;
  nextPageDataCallTrigger: (requestData: any) => void;
}) => {
  const { title, data } = recentSearchInfo;
  const analytics = getAnalytics();
  useEffect(() => {
    analytics?.trackCommonLandingEvents({
      actionType: "recent_searches_seen",
    });
  }, []);
  const formatTravelInfo = (
    travellerNum: number,
    travelDuration: { startDate: number; endDate: number }
  ) => {
    const startDate = convertUnixToDate(travelDuration.startDate);
    const endDate = convertUnixToDate(travelDuration.endDate);

    if (!startDate || !endDate) {
      return `${travellerNum} Travellers`;
    }

    const startDay = startDate.getDate();
    const startMonth = startDate.getMonth();
    const startYear = startDate.getFullYear();
    const endDay = endDate.getDate();
    const endMonth = endDate.getMonth();
    const endYear = endDate.getFullYear();
    const yearSuffix = (year: number) => year.toString().slice(-2);

    let dateRange: string;

    if (
      startDay === endDay &&
      startMonth === endMonth &&
      startYear === endYear
    ) {
      // Same date: 11 Jan' 26
      dateRange = `${startDay} ${MONTHS[startMonth]}' ${yearSuffix(startYear)}`;
    } else if (startMonth === endMonth && startYear === endYear) {
      // Same month & year: 11-12 Jan' 26
      dateRange = `${startDay}-${endDay} ${MONTHS[startMonth]}' ${yearSuffix(
        startYear
      )}`;
    } else if (startYear === endYear) {
      // Same year, different month: 11 Jan'-12 Jul' 26
      dateRange = `${startDay} ${MONTHS[startMonth]}'-${endDay} ${
        MONTHS[endMonth]
      }' ${yearSuffix(startYear)}`;
    } else {
      // Different year: 11 Jan' 26-11 Jan' 27
      dateRange = `${startDay} ${MONTHS[startMonth]}' ${yearSuffix(
        startYear
      )}-${endDay} ${MONTHS[endMonth]}' ${yearSuffix(endYear)}`;
    }

    return `${travellerNum} Travellers, ${dateRange}`;
  };

  const MAX_COUNTRY_CHARS = 22;

  const formatCountryName = (countryName: string): string => {
    if (!countryName) return "";

    const countries = countryName.split(",").map((c) => c.trim());
    const totalCountries = countries.length;

    if (totalCountries === 1) {
      // Single country - truncate if too long
      if (countryName.length > MAX_COUNTRY_CHARS) {
        return countryName.slice(0, MAX_COUNTRY_CHARS - 3) + "...";
      }
      return countryName;
    }

    // Multiple countries
    let result = "";
    let includedCount = 0;

    for (let i = 0; i < totalCountries; i++) {
      const country = countries[i];
      const remaining = totalCountries - includedCount - 1;
      const suffix = remaining > 0 ? `..+${remaining}` : "";
      const separator = includedCount > 0 ? "," : "";
      const potentialResult = result + separator + country + suffix;

      if (potentialResult.length > MAX_COUNTRY_CHARS) {
        // Can't fit this country fully
        if (includedCount === 0) {
          // First country itself is too long - truncate it
          const suffixForAll = `..+${totalCountries - 1}`;
          const availableChars = MAX_COUNTRY_CHARS - suffixForAll.length;
          return country.slice(0, availableChars) + suffixForAll;
        }
        // Show what we have so far with remaining count
        const remainingCount = totalCountries - includedCount;
        return result + `..+${remainingCount}`;
      }

      result = result + separator + country;
      includedCount++;
    }

    return result;
  };

  const handleRecentSearchClick = (item: any, index: number) => {
    analytics?.trackCommonLandingEvents({
      actionType: `recent_search_${index}_clicked`,
    });
    nextPageDataCallTrigger(buildRecentSearchNextPageRequest(item));
  };

  return (
    <FlexComp gap="8px" alignItems="center">
      <FormattedText
        value={title}
        baseStyle={{ fontSize: "12px", fontWeight: 700, color: theme.greyText }}
      />
      {data.map((item, index) => (
        <RecentSearchCard onClick={() => handleRecentSearchClick(item, index)}>
          <FormattedText
            value={item.title}
            baseStyle={{ color: theme.blueText }}
          />
          <CountryNameText>
            <FormattedText
              value={formatCountryName(item.countryName)}
              baseStyle={{ fontWeight: 700, color: theme.blackText }}
            />
          </CountryNameText>
          <FormattedText
            value={formatTravelInfo(item.travellerNum, item.travelDuration)}
            baseStyle={{ color: theme.greyText }}
          />
        </RecentSearchCard>
      ))}
    </FlexComp>
  );
};

export default RecentSearches;
